// Aby włączyć tryb debugowania wpisz w konsoli przeglądarki poniższe polecenie na ekranie podsumowania już po zaznaczeniu CAPTCHA :
// window.__FORCE_DEBUG__ = true

import { CardMedia, Typography } from "@material-ui/core";
import { isRejected } from "@reduxjs/toolkit";
import { push } from "connected-react-router";
import { andThen, pipe } from "ramda";
import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import icon from "../../../assets/icons/error.svg";
import { Heading } from "../../../components/Heading";
import { selectImages } from "../../../components/ImageUploader/images.slice";
import { Images } from "../../../components/ImageUploader/types";
import { convertBlobsToFiles } from "../../../components/ImageUploader/utilities/convertBlobsToFiles";
import {
  selectFormType,
  selectPrevRoute,
} from "../../../components/formSettings.slice";
import { useHideFooter, usePreventRefresh } from "../../../hooks";
import { Url } from "../../../router/enums";
import { mapProgressRouteToSuccess } from "../../../router/utilities";
import { RootState } from "../../../store";
import {
  selectACFormData,
  selectOCVehicleFormData,
} from "../../../store/rootSelectors";
import { GenericScreen } from "../../GenericScreen";
import {
  doesErrorOccured,
  generatePDF,
  isCaseSentSuccessfully,
  postTextData,
  postImages,
  fulfillSender,
} from "../sender.slice";

import styles from "./Progress.module.scss";

type LogLevel = "log" | "info" | "warn" | "error" | "group" | "groupEnd";
type LogEntry = {
  t: string;
  level: LogLevel;
  title?: string;
  data?: unknown[];
};

declare global {
  interface Window {
    __senderLogs__?: LogEntry[];
    __FORCE_DEBUG__?: boolean;
    __SENDER_DEBUG_TIP_SHOWN__?: boolean;
  }
}

const COLORS = {
  header: "#0f3549",
  info: "#008282",
  warn: "#ffd540",
  text: "#0f3549",
};
const css = {
  header: `color:${COLORS.header}; font-size:16px; font-weight:700;`,
  info: `color:${COLORS.info}; font-size:13px; font-weight:600;`,
  warn: `color:${COLORS.text}; background:${COLORS.warn}; font-size:13px; font-weight:700; padding:1px 6px; border-radius:4px;`,
  dim: `color:${COLORS.text}; font-size:12px;`,
  big: `color:#ffd540; font-size:16px; font-weight:700;`,
};

const LOG_KEY = "__senderLogs__";

type ConsoleLike = {
  log: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
  group?: (...args: unknown[]) => void;
  groupEnd?: (...args: unknown[]) => void;
};

function getConsole(): ConsoleLike {
  if (typeof window !== "undefined" && window.console)
    return window.console as ConsoleLike;
  return {
    log: () => undefined,
    info: () => undefined,
    warn: () => undefined,
    error: () => undefined,
    group: () => undefined,
    groupEnd: () => undefined,
  };
}

function isDebugOn(): boolean {
  try {
    return window.__FORCE_DEBUG__ === true;
  } catch (_e) {
    return false;
  }
}

function recordLog(level: LogLevel, title?: string, ...data: unknown[]) {
  if (!isDebugOn()) return;
  const entry: LogEntry = { t: new Date().toISOString(), level, title, data };
  try {
    const next = [...(window.__senderLogs__ ?? []), entry];
    window.__senderLogs__ = next;
    try {
      const MAX = 5000;
      const toStore = next.length > MAX ? next.slice(next.length - MAX) : next;
      sessionStorage.setItem(LOG_KEY, JSON.stringify(toStore));
    } catch (_e) {
      void 0;
    }
  } catch (_e) {
    void 0;
  }
}

function styled(
  level: "info" | "warn" | "header" | "dim" | "big",
  title: string,
  ...rest: unknown[]
) {
  const c = getConsole();
  const style =
    level === "info"
      ? css.info
      : level === "warn"
      ? css.warn
      : level === "header"
      ? css.header
      : level === "big"
      ? css.big
      : css.dim;
  try {
    c.log(`%c${title}`, style, ...rest);
  } catch (_e) {
    c.log(title, ...rest);
  }
}

function groupOpen(title: string) {
  recordLog("group", title);
  if (!isDebugOn()) return;
  const c = getConsole();
  try {
    c.group ? c.group(title) : c.log(title);
  } catch (_e) {
    void 0;
  }
}

function groupClose() {
  recordLog("groupEnd");
  if (!isDebugOn()) return;
  const c = getConsole();
  try {
    if (c.groupEnd) c.groupEnd();
  } catch (_e) {
    void 0;
  }
}

function logInfo(title: string, ...data: unknown[]) {
  recordLog("info", title, ...data);
  if (isDebugOn()) styled("info", title, ...data);
}
function logWarn(title: string, ...data: unknown[]) {
  recordLog("warn", title, ...data);
  if (isDebugOn()) styled("warn", title, ...data);
}
function logDim(title: string, ...data: unknown[]) {
  recordLog("log", title, ...data);
  if (isDebugOn()) styled("dim", title, ...data);
}

function tipOnceWhenDebugOff() {
  if (isDebugOn()) return;
  try {
    if (window.__SENDER_DEBUG_TIP_SHOWN__) return;
    window.__SENDER_DEBUG_TIP_SHOWN__ = true;
  } catch (_e) {
    void 0;
  }
  try {
    getConsole().log(
      "%cINFO - Aby włączyć trby debugowania wysyłki danych do Likwidacji przejrzyj komentarze w pliku Progress.tsx",
      css.info
    );
  } catch (_e) {
    getConsole().log(
      "INFO - Aby włączyć trby debugowania wysyłki danych do Likwidacji przejrzyj komentarze w pliku Progress.tsx"
    );
  }
}

const getActionLabel = (fn: unknown): string => {
  try {
    if (typeof fn === "function") {
      const anyFn: any = fn;
      if (typeof anyFn.typePrefix === "string") return anyFn.typePrefix;
      const maybe = anyFn();
      if (maybe && typeof maybe === "object") {
        const m: any = maybe;
        if (typeof m.typePrefix === "string") return m.typePrefix;
        if (typeof m.type === "string") return m.type;
      }
    }
  } catch (_e) {
    void 0;
  }
  return "unknown";
};

const prepareImageToUpload = (images: Record<string, Images>) => {
  const data: { [key: string]: Array<string> } = {};
  for (const [key, values] of Object.entries(images))
    data[key] = Object.values(values).map((image) => image.src);
  return data;
};

const convertBlobsToImages = async (images: {
  [key: string]: Array<string>;
}) => {
  const data: { [key: string]: Array<string> } = {};
  for (const [key, values] of Object.entries(images))
    data[key] = await convertBlobsToFiles(values);
  return data;
};

const convertToDispatchActions = (images: { [key: string]: Array<string> }) => {
  const actions = Object.keys(images).map(
    (imageKey) => () => postImages({ type: imageKey, images: images[imageKey] })
  );
  return actions;
};

const prepareUploadImagesAction = pipe(
  prepareImageToUpload,
  convertBlobsToImages,
  andThen(convertToDispatchActions)
);

type Endpoints = {
  apiEndpoint: string;
  apiPDFEndpoint: string;
  apiPostalCodesAndModelsEndpoint: string;
};
const DEFAULT_PATHS = {
  apiEndpoint: "/Likwidacja/api/FNOL",
  apiPDFEndpoint: "/Likwidacja/FNOL",
  apiPostalCodesAndModelsEndpoint: "/Likwidacja/Api/",
};

function buildFallbackFromLocation(loc: Location = window.location): Endpoints {
  const origin = loc.origin;
  return {
    apiEndpoint: origin + DEFAULT_PATHS.apiEndpoint,
    apiPDFEndpoint: origin + DEFAULT_PATHS.apiPDFEndpoint,
    apiPostalCodesAndModelsEndpoint:
      origin + DEFAULT_PATHS.apiPostalCodesAndModelsEndpoint,
  };
}

async function loadEndpointsWithFallback(): Promise<Endpoints> {
  const fallback = buildFallbackFromLocation();
  try {
    const base = process.env.PUBLIC_URL || "";
    const url = `${base}/appData.json?ts=${Date.now()}`;
    const res = await fetch(url, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) return fallback;
    const json = (await res.json()) as { config?: Partial<Endpoints> };
    const cfg = json.config ?? {};
    return {
      apiEndpoint: cfg.apiEndpoint || fallback.apiEndpoint,
      apiPDFEndpoint: cfg.apiPDFEndpoint || fallback.apiPDFEndpoint,
      apiPostalCodesAndModelsEndpoint:
        cfg.apiPostalCodesAndModelsEndpoint ||
        fallback.apiPostalCodesAndModelsEndpoint,
    };
  } catch (_e) {
    return fallback;
  }
}

export type SendingProgressProps = {
  readonly formDataSelector: (
    store: RootState
  ) =>
    | ReturnType<typeof selectACFormData>
    | ReturnType<typeof selectOCVehicleFormData>;
};

export const SendingProgress = ({
  formDataSelector,
}: SendingProgressProps): JSX.Element => {
  const prevRoute = useSelector(selectPrevRoute);
  const formType = useSelector(selectFormType);
  const images = useSelector(selectImages);
  const formData = useSelector(formDataSelector);
  const hasError = useSelector(doesErrorOccured);
  const sentSucessfully = useSelector(isCaseSentSuccessfully);
  const dispatch = useDispatch();

  usePreventRefresh();
  useHideFooter();

  useEffect(() => {
    tipOnceWhenDebugOff();

    const handleSender = async () => {
      if (isDebugOn())
        styled(
          "big",
          "GIELK - WŁĄCZONO DEBUGOWANIE WYSYŁKI DANYCH - WERSJA 1.1 - 11.02.2026"
        );
      groupOpen("=== TRYB DEBUG WYSYŁKI DO LIKWIDACJI ===");
      try {
        logInfo("Formularz — typ", formType);
        logInfo("Formularz — dane", formData);
        logInfo("Załączone zdjęcia", images);
        const endpoints = await loadEndpointsWithFallback();
        logInfo("Endpointy użyte w żądaniu", endpoints);

        const uploadImagesActions = await prepareUploadImagesAction(images);

        const actions: Array<() => unknown> = [
          () => (postTextData as any)({ data: formData, formType, endpoints }),
          ...uploadImagesActions,
          () => (generatePDF as any)({ formType, endpoints }),
          fulfillSender,
        ];

        const planned = actions.map((fn) => getActionLabel(fn));
        logInfo("Zaplanowane akcje (kolejność)", planned);

        for (const actionFactory of actions) {
          const label = getActionLabel(actionFactory);
          groupOpen(`Wysyłanie akcji: ${label}`);

          if (
            label === (postTextData as any).typePrefix ||
            label ===
              getActionLabel(() =>
                (postTextData as any)({ data: formData, formType })
              )
          ) {
            logInfo("Zapytanie POST — dane", {
              data: formData,
              formType,
              endpoints,
            });
          }

          try {
            const actionInstance = actionFactory();
            const response = await (dispatch as any)(actionInstance);
            logDim("Odpowiedź dispatch", response);

            if (isRejected(response)) {
              logWarn("❌ Akcja odrzucona", response);
              if (response && (response as any).error)
                logWarn(
                  "Szczegóły błędu (Redux error)",
                  (response as any).error
                );
              if (response && (response as any).payload)
                logWarn("Treść błędu (payload)", (response as any).payload);
              groupClose();
              break;
            }
          } catch (err) {
            logWarn("Błąd podczas dispatch", err);
            groupClose();
            break;
          }

          groupClose();
        }
      } catch (outerErr) {
        logWarn("Błąd krytyczny podczas wysyłki", outerErr);
      } finally {
        groupClose();
      }
    };
    void handleSender();
  }, [dispatch, formData, formType, images]);

  useEffect(() => {
    if (hasError) {
      try {
        if (isDebugOn()) {
          const proceed = window.confirm(
            "DEBUG: Wykryto błąd wysyłki. Przejść na stronę błędu?"
          );
          if (!proceed) {
            logWarn("Nawigacja wstrzymana przez użytkownika (debug)");
            return;
          }
        }
      } catch (_e) {
        void 0;
      }
      logWarn("Nawigacja: błąd wysyłki (przejście na stronę błędu)");
      (function mark() {
        recordLog("warn", "MARKER", {
          reason: "hasError true",
          ts: Date.now(),
        });
      })();
      dispatch(push(Url.SENDING_ERROR));
    }
    if (sentSucessfully) {
      logInfo("Nawigacja: sukces — przejście na ekran potwierdzenia", {
        from: prevRoute,
        to: mapProgressRouteToSuccess[prevRoute],
      });
      dispatch(push(mapProgressRouteToSuccess[prevRoute]));
    }
  }, [prevRoute, dispatch, hasError, sentSucessfully]);

  return (
    <GenericScreen>
      <CardMedia component="img" src={icon} className={styles.icon} />
      <Heading
        align="center"
        color="secondary"
        component="h1"
        variant="h2"
        spacingBottom={{ xs: 4, sm: 6 }}
      >
        Trwa wysyłanie formularza
      </Heading>
      <Typography paragraph align="center">
        Proszę czekać - trwa wysyłanie zgłoszenia.
      </Typography>
    </GenericScreen>
  );
};
